import { routing } from '@/src/i18n/routing';
import { hasAreaSegmentShape, isAllRentalsSlug } from './allRentalsSlug';
import { isNearMeSlugCandidate } from './near-me';
import { buildSrpPagePath, getSrpPathPage, SRP_PAGE_SEGMENT, stripSrpPageSegment } from './srp-pagination';

export type SrpPath = {
  locale?: string;
  /** Absent on a near-me URL, whose area comes from geolocation. */
  resourceId?: string;
  /** The slug the page searches on: the all-rentals slug on the bare area URL. */
  slug: string;
  page: number | null;
};

/**
 * Reads an SRP pathname: `/{area}/{slug}`, the bare `/{area}` (all rentals) and a near-me
 * `/{slug}`, each with an optional locale prefix and `/page/N` tail. Null for any other shape.
 */
export function parseSrpPath(
  pathname: string,
  allRentalsSlug: string,
  locales: ReadonlyArray<string> = routing.locales,
): SrpPath | null {
  const segments = (pathname.split(/[?#]/)[0] ?? '').split('/').filter(Boolean);
  const locale = segments[0] && locales.includes(segments[0]) ? segments[0] : undefined;
  const route = `/${(locale ? segments.slice(1) : segments).join('/')}`;
  const page = getSrpPathPage(route);
  const [first, second, ...rest] = stripSrpPageSegment(route).split('/').filter(Boolean);
  if (!first || first === 'near-me' || rest.length > 0) return null;

  if (second === undefined) {
    if (isNearMeSlugCandidate(first)) return { locale, slug: first, page };
    return hasAreaSegmentShape(first) ? { locale, resourceId: first, slug: allRentalsSlug, page } : null;
  }
  if (second === SRP_PAGE_SEGMENT || second === 'pad' || second === 'building') return null;
  return { locale, resourceId: first, slug: second, page };
}

/** An area SRP path; the all-rentals slug (or none) is the bare area URL. */
export function buildSrpPath(resourceId: string, slug?: string | null, page = 1): string {
  return buildSrpPagePath(slug && !isAllRentalsSlug(slug) ? `/${resourceId}/${slug}` : `/${resourceId}`, page);
}

/**
 * The bare area page's canonical is itself. Its area segment comes from the backend canonical,
 * which the bare page receives with the slug it searched on (`/la-ca/apartments-for-rent`).
 */
export function bareAreaCanonicalPath(beCanonicalPath: string | undefined, resourceId: string, page: number): string {
  return buildSrpPath(beCanonicalPath?.split('/').find(Boolean) ?? resourceId, null, page);
}
